import React, { useState } from 'react';
import { User, Phone, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuthStore } from '@/store/authStore';
import { validatePhoneNumber } from '@/utils/validation';

interface EditMode {
  type: 'username' | 'phone' | 'password';
  value: string;
  newValue?: string; // For password confirmation
}

export function UserProfilePopup() {
  const { user, updateUserPhone, updateUsername, updateUserPassword } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [editMode, setEditMode] = useState<EditMode | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!editMode || !user) return;
    setError(null);

    try {
      switch (editMode.type) {
        case 'username':
          if (editMode.value.trim().length < 3) {
            setError('يجب أن يحتوي الاسم على 3 أحرف على الأقل');
            return;
          }
          updateUsername(user.id, editMode.value);
          break;

        case 'phone':
          const phoneValidation = validatePhoneNumber(editMode.value);
          if (phoneValidation !== true) {
            setError(phoneValidation);
            return;
          }
          updateUserPhone(user.id, editMode.value);
          break;

        case 'password':
          if (!editMode.newValue || editMode.value !== editMode.newValue) {
            setError('كلمة المرور غير متطابقة');
            return;
          }
          if (editMode.value.length < 6) {
            setError('يجب أن تحتوي كلمة المرور على 6 أحرف على الأقل');
            return;
          }
          updateUserPassword(user.id, editMode.value);
          break;
      }
      setEditMode(null);
    } catch (error) {
      setError('حدث خطأ أثناء التحديث');
    }
  };

  if (!user) return null;

  return (
    <>
      <div 
        onClick={() => setIsOpen(true)}
        className="space-y-4 cursor-pointer hover:bg-gray-50 p-4 rounded-lg transition-colors"
      >
        <div className="flex items-center">
          <span className="flex-shrink-0 h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-6 w-6 text-primary" />
          </span>
          <div className="mr-3">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-900">{user.username}</p>
              {user.role === 'admin' && (
                <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                  مدير
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1" dir="ltr">
              {user.phoneNumber}
            </p>
          </div>
        </div>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>الملف الشخصي</DialogTitle>
          </DialogHeader>

          {!editMode ? (
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>الاسم</Label>
                    <p className="text-sm text-gray-600">{user.username}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditMode({ type: 'username', value: user.username })}
                  >
                    تعديل
                  </Button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>رقم الجوال</Label>
                    <p className="text-sm text-gray-600" dir="ltr">{user.phoneNumber}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditMode({ type: 'phone', value: user.phoneNumber })}
                  >
                    تعديل
                  </Button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>كلمة المرور</Label>
                    <p className="text-sm text-gray-600">********</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditMode({ type: 'password', value: '', newValue: '' })}
                  >
                    تغيير
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {editMode.type === 'password' ? (
                <>
                  <div className="space-y-2">
                    <Label>كلمة المرور الجديدة</Label>
                    <Input
                      type="password"
                      value={editMode.value}
                      onChange={(e) => setEditMode({ ...editMode, value: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>تأكيد كلمة المرور</Label>
                    <Input
                      type="password"
                      value={editMode.newValue || ''}
                      onChange={(e) => setEditMode({ ...editMode, newValue: e.target.value })}
                    />
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label>
                    {editMode.type === 'username' ? 'الاسم' : 'رقم الجوال'}
                  </Label>
                  <Input
                    type={editMode.type === 'phone' ? 'tel' : 'text'}
                    dir={editMode.type === 'phone' ? 'ltr' : undefined}
                    className={editMode.type === 'phone' ? 'text-left' : undefined}
                    value={editMode.value}
                    onChange={(e) => setEditMode({ ...editMode, value: e.target.value })}
                  />
                </div>
              )}

              {error && (
                <p className="text-sm text-red-500">{error}</p>
              )}

              <DialogFooter className="sm:justify-start gap-2">
                <Button onClick={handleSubmit}>حفظ</Button>
                <Button variant="ghost" onClick={() => setEditMode(null)}>
                  إلغاء
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}